/**
 * React Query hooks for /api/v1/downhole/jobs (api#1055, Directional & MWD
 * well jobs). Same rules as hooks/useDownhole.ts: every read keeps `meta`
 * (the tenant zone formats every stamp) and spreads LOAD_FAILURE_QUERY_OPTIONS.
 *
 * EVERY READ TAKES `enabled`. The API answers 409 DD_MODE_OFF to every door
 * while the company's dd_mwd mode is off, so a screen passes
 * `useDownholeFeature('dd_mwd')` and asks nothing until the mode is on.
 *
 * A write answers the job detail, so it is put straight into the detail cache
 * (keeping the meta the read brought, as writes to /jobs/:id/assemblies carry
 * none) and the list is invalidated. Attaching or removing an assembly also
 * moves that assembly's customer, well and runs, so the assembly and run
 * caches are invalidated too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { setQueryDataMapped } from '../lib/queryClient';
import { unwrapDownhole, unwrapDownholeList } from './useDownhole';
import type {
  DhCrewSpell,
  DhDayPrice,
  DhDayWrite,
  DhJobDay,
  DhCrewWrite,
  DhJob,
  DhJobBilling,
  DhJobDetail,
  DhJobTransitionWrite,
  DhJobWrite,
  DownholeEnvelope,
  DownholeResult,
} from '../types/downhole';

export interface DhJobListParams {
  status?: string;
  customer_id?: string;
  q?: string;
}

export const downholeJobKeys = {
  list: (params?: DhJobListParams) => ['downhole', 'jobs', params ?? {}] as const,
  detail: (id: string) => ['downhole', 'job', id] as const,
};

export function useDownholeJobs(params: DhJobListParams | undefined, enabled: boolean) {
  return useQuery({
    queryKey: downholeJobKeys.list(params),
    queryFn: async () =>
      unwrapDownholeList<DhJob>((await apiClient.get<DownholeEnvelope<DhJob[]>>('/downhole/jobs', { params })).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeJob(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: downholeJobKeys.detail(id ?? ''),
    queryFn: async () =>
      unwrapDownhole<DhJobDetail>((await apiClient.get<DownholeEnvelope<DhJobDetail>>(`/downhole/jobs/${id}`)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useJobWrite<V>(
  jobIdOf: (vars: V) => string | null,
  call: (vars: V) => Promise<{ data: unknown }>,
  options: { touchesAssemblies?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapDownhole<DhJobDetail>((await call(vars)).data),
    onSuccess: (result, vars) => {
      const id = jobIdOf(vars) ?? result.data?.id ?? null;
      if (id && result.data) {
        const prev = qc.getQueryData<DownholeResult<DhJobDetail | null>>(downholeJobKeys.detail(id));
        // Keep the read's meta when the write brought none (the assembly routes).
        const meta = result.meta.time_zone === null && prev?.meta ? prev.meta : result.meta;
        setQueryDataMapped<DownholeResult<DhJobDetail | null>>(qc, downholeJobKeys.detail(id), { data: result.data, meta });
        // A step, a stamp correction or an assembly move changes which days the
        // job has and what each one prices to. Found by the fe#1019 rendered
        // check: after Arrived on location the Days panel still read "No days
        // yet" until a reload.
        qc.invalidateQueries({ queryKey: ['downhole', 'job', id, 'days'] });
        qc.invalidateQueries({ queryKey: ['downhole', 'job', id, 'day'] });
        qc.invalidateQueries({ queryKey: ['downhole', 'tickets'] });
      }
      qc.invalidateQueries({ queryKey: ['downhole', 'jobs'] });
      if (options.touchesAssemblies) {
        qc.invalidateQueries({ queryKey: ['downhole', 'assemblies'] });
        qc.invalidateQueries({ queryKey: ['downhole', 'assembly'] });
        qc.invalidateQueries({ queryKey: ['downhole', 'runs'] });
      }
    },
  });
}

export function useCreateJob() {
  return useJobWrite<DhJobWrite>(
    () => null,
    (body) => apiClient.post<DownholeEnvelope<DhJobDetail>>('/downhole/jobs', body),
  );
}

export function useUpdateJob() {
  return useJobWrite<{ id: string; body: DhJobWrite }>(
    (v) => v.id,
    (v) => apiClient.patch<DownholeEnvelope<DhJobDetail>>(`/downhole/jobs/${v.id}`, v.body),
  );
}

export function useJobTransition() {
  return useJobWrite<{ id: string; body: DhJobTransitionWrite }>(
    (v) => v.id,
    (v) => apiClient.post<DownholeEnvelope<DhJobDetail>>(`/downhole/jobs/${v.id}/transition`, v.body),
  );
}

export function useAttachJobAssembly() {
  return useJobWrite<{ id: string; assembly_id: string }>(
    (v) => v.id,
    (v) => apiClient.post<DownholeEnvelope<DhJobDetail>>(`/downhole/jobs/${v.id}/assemblies`, { assembly_id: v.assembly_id }),
    { touchesAssemblies: true },
  );
}

export function useDetachJobAssembly() {
  return useJobWrite<{ id: string; assembly_id: string }>(
    (v) => v.id,
    (v) => apiClient.delete<DownholeEnvelope<DhJobDetail>>(`/downhole/jobs/${v.id}/assemblies/${v.assembly_id}`),
    { touchesAssemblies: true },
  );
}

/** DELETE answers `{ success }` with no job, so the detail cache is dropped, not written. */
export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<{ success: boolean }>(`/downhole/jobs/${id}`);
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: downholeJobKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['downhole', 'jobs'] });
    },
  });
}

// ─── Crew (api#1057) ─────────────────────────────────────────────────────────
// The crew list rides on GET /jobs/:id (`crew`), so a crew write refreshes the
// job detail rather than keeping a second copy. A write answers one spell, not
// the job, so nothing is written into the detail cache by hand.

function useCrewWrite<V extends { jobId: string }>(call: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => {
      await call(vars);
      return vars.jobId;
    },
    onSuccess: (jobId) => {
      qc.invalidateQueries({ queryKey: downholeJobKeys.detail(jobId) });
    },
  });
}

export function useAddCrew() {
  return useCrewWrite<{ jobId: string; body: DhCrewWrite }>((v) =>
    apiClient.post<DownholeEnvelope<DhCrewSpell>>(`/downhole/jobs/${v.jobId}/crew`, v.body),
  );
}

export function useUpdateCrew() {
  return useCrewWrite<{ jobId: string; crewId: string; body: DhCrewWrite }>((v) =>
    apiClient.patch<DownholeEnvelope<DhCrewSpell>>(`/downhole/jobs/${v.jobId}/crew/${v.crewId}`, v.body),
  );
}

export function useRemoveCrew() {
  return useCrewWrite<{ jobId: string; crewId: string }>((v) =>
    apiClient.delete<{ success: boolean }>(`/downhole/jobs/${v.jobId}/crew/${v.crewId}`),
  );
}

// ─── Days (api#1062) ─────────────────────────────────────────────────────────
// A day's write re-prices it and can send its ticket back to draft, so a save
// refreshes the day list, that day's price, the job and the ticket lists.

export const downholeDayKeys = {
  list: (jobId: string) => ['downhole', 'job', jobId, 'days'] as const,
  day: (jobId: string, date: string) => ['downhole', 'job', jobId, 'day', date] as const,
};

export function useJobDays(jobId: string, enabled: boolean) {
  return useQuery({
    queryKey: downholeDayKeys.list(jobId),
    queryFn: async () =>
      unwrapDownholeList<DhJobDay>((await apiClient.get<DownholeEnvelope<DhJobDay[]>>(`/downhole/jobs/${jobId}/days`)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useJobDay(jobId: string, date: string | null) {
  return useQuery({
    queryKey: downholeDayKeys.day(jobId, date ?? ''),
    queryFn: async () =>
      unwrapDownhole<DhDayPrice>((await apiClient.get<DownholeEnvelope<DhDayPrice>>(`/downhole/jobs/${jobId}/days/${date}`)).data),
    enabled: !!date,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useDayRefresh() {
  const qc = useQueryClient();
  return (jobId: string) => {
    // Prefix ['downhole','job',jobId] covers the job detail, its days and every day price.
    qc.invalidateQueries({ queryKey: ['downhole', 'job', jobId] });
    qc.invalidateQueries({ queryKey: ['downhole', 'tickets'] });
  };
}

export function useUpdateJobDay() {
  const refresh = useDayRefresh();
  return useMutation({
    mutationFn: async (v: { jobId: string; date: string; body: DhDayWrite }) => {
      const res = await apiClient.put<DownholeEnvelope<DhDayPrice> & { reverted_tickets?: string[] }>(
        `/downhole/jobs/${v.jobId}/days/${v.date}`,
        v.body,
      );
      return { revertedTickets: Array.isArray(res.data?.reverted_tickets) ? res.data.reverted_tickets : [] };
    },
    onSuccess: (_r, v) => refresh(v.jobId),
  });
}

/** POST …/days/:date/ticket: the day's draft ticket. Answers the ticket. */
export function useOpenDayTicket() {
  const refresh = useDayRefresh();
  return useMutation({
    mutationFn: async (v: { jobId: string; date: string; body: { customer_notes?: string; notes?: string } }) => {
      const res = await apiClient.post<DownholeEnvelope<{ id: string; ticket_number: string }>>(
        `/downhole/jobs/${v.jobId}/days/${v.date}/ticket`,
        v.body,
      );
      return unwrapDownhole<{ id: string; ticket_number: string }>(res.data);
    },
    onSuccess: (_r, v) => refresh(v.jobId),
  });
}

// ─── Billing (api#1090) ──────────────────────────────────────────────────────
// Office roles only (admin, office_staff, dispatcher): it shows invoice money.
// The invoice itself is made through useGenerateInvoice (the one invoice door),
// whose success invalidates ['downhole','job'], which covers this key.

export const downholeBillingKey = (jobId: string) => ['downhole', 'job', jobId, 'billing'] as const;

export function useJobBilling(jobId: string, enabled: boolean) {
  return useQuery({
    queryKey: downholeBillingKey(jobId),
    queryFn: async () =>
      unwrapDownhole<DhJobBilling>((await apiClient.get<DownholeEnvelope<DhJobBilling>>(`/downhole/jobs/${jobId}/billing`)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
